import { useEffect } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
} from '@mui/material';
import { useBlocker } from 'react-router-dom';
import DialogAriaWrapper from '../DialogAriaWrapper';

interface NavigationBlockerDialogProps {
  when?: boolean;
  title?: string;
  message?: string;
  subText?: JSX.Element;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  /**
   * Enables the native browser confirmation dialog for navigation events
   * that cannot be intercepted by `useBlocker`.
   *
   * This includes scenarios like page reloads, browser tab/window closing, or navigating
   * to external sites outside the React Router context.
   *
   * When set to `true`, the browser will prompt the user with a native alert
   */
  blockBrowserActions?: boolean;
  /**
   * Forces the dialog to open even when navigation is not detected by `useBlocker`.
   *
   * This is useful for custom navigation flows that don't involve a route change,
   * such as tab switching or internal state transitions within a single page.
   *
   * When set to `true`, the dialog will appear as if a navigation attempt occurred,
   * allowing you to reuse the same confirmation flow for non-route interactions.
   */
  manualTrigger?: boolean;
}

export const NavigationBlockerDialog = ({
  when = true,
  title = 'Are you sure?',
  message = 'You have unsaved changes that will be lost if you proceed.',
  confirmText = 'Proceed',
  subText,
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  blockBrowserActions = false,
  manualTrigger = false,
}: NavigationBlockerDialogProps) => {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when && currentLocation.pathname !== nextLocation.pathname,
  );

  const handleCancel = () => {
    blocker.reset?.();
    onCancel?.();
  };

  const handleProceed = () => {
    blocker.proceed?.();
    onConfirm?.();
  };

  const showContent = !!message || !!subText;

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!when || !blockBrowserActions) return;

      if (blocker.state !== 'blocked') {
        e.preventDefault();
      }
    };

    if (blockBrowserActions) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [when, blockBrowserActions]);

  const isBlockedByRouter = blocker.state === 'blocked';
  const shouldShowDialog = manualTrigger || isBlockedByRouter;

  return (
    <DialogAriaWrapper id='navigation-blocker' open={shouldShowDialog}>
      <DialogTitle sx={{ p: 2, textAlign: 'center' }}>{title}</DialogTitle>
      <Divider sx={{ mx: 2 }} />
      {showContent && (
        <>
          <DialogContent
            sx={{ textAlign: 'center', py: 5, width: 600, maxWidth: '90%', mx: 'auto' }}
          >
            <DialogContentText component='div'>{message}</DialogContentText>
            {subText && <DialogContentText component='div'>{subText}</DialogContentText>}
          </DialogContent>
          <Divider sx={{ mx: 2 }} />
        </>
      )}
      <DialogActions sx={{ p: 2, display: 'flex', gap: 2 }}>
        <Button aria-label='confirm' variant='contained' onClick={handleProceed} fullWidth>
          {confirmText}
        </Button>
        <Button
          aria-label='cancel'
          onClick={handleCancel}
          variant='outlined'
          color='primary'
          fullWidth
        >
          {cancelText}
        </Button>
      </DialogActions>
    </DialogAriaWrapper>
  );
};
